/**
 * <image-carousel> — Lightweight auto-rotating image carousel
 * Used inside the Scrolling Banner section for multi-image blocks.
 *
 * Attributes:
 *   data-interval="3000"  — autoplay interval in ms (default 3000)
 *   data-autoplay="true"  — enable autoplay (default true)
 */
class ImageCarousel extends HTMLElement {
  constructor() {
    super();
    this.currentIndex = 0;
    this.timer = null;
  }

  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('[data-slide]'));
    this.dots = Array.from(this.querySelectorAll('.msc-dot'));
    this.prevBtn = this.querySelector('.msc-prev');
    this.nextBtn = this.querySelector('.msc-next');

    if (this.slides.length < 2) return;

    this.interval = parseInt(this.dataset.interval) || 3000;
    this.autoplayEnabled = this.dataset.autoplay !== 'false';

    this.prevBtn?.addEventListener('click', () => this.prev());
    this.nextBtn?.addEventListener('click', () => this.next());
    this.dots.forEach((dot, i) => {
      dot.addEventListener('click', () => this.goTo(i));
    });

    this.addEventListener('mouseenter', () => this.stopAutoplay());
    this.addEventListener('mouseleave', () => {
      if (this.autoplayEnabled && this.isVisible) this.startAutoplay();
    });

    this.observer = new IntersectionObserver(
      ([entry]) => {
        this.isVisible = entry.isIntersecting;
        if (entry.isIntersecting && this.autoplayEnabled) {
          this.startAutoplay();
        } else {
          this.stopAutoplay();
        }
      },
      { threshold: 0.3 }
    );
    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.stopAutoplay();
    this.observer?.disconnect();
  }

  goTo(index) {
    if (index === this.currentIndex) return;
    this.slides[this.currentIndex]?.classList.remove('is-active');
    this.dots[this.currentIndex]?.setAttribute('aria-current', 'false');

    this.currentIndex = ((index % this.slides.length) + this.slides.length) % this.slides.length;

    this.slides[this.currentIndex]?.classList.add('is-active');
    this.dots[this.currentIndex]?.setAttribute('aria-current', 'true');

    if (this.autoplayEnabled && this.isVisible) {
      this.stopAutoplay();
      this.startAutoplay();
    }
  }

  next() {
    this.goTo(this.currentIndex + 1);
  }

  prev() {
    this.goTo(this.currentIndex - 1);
  }

  startAutoplay() {
    if (this.timer) return;
    this.timer = setInterval(() => this.next(), this.interval);
  }

  stopAutoplay() {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }
}

customElements.define('image-carousel', ImageCarousel);
